import React from "react";
import { ApiError } from "@atlas/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, MessageSquare } from "lucide-react";
import { fetchMyFeedbackDetail, replyToMyFeedback } from "../api/feedback";
import { Badge } from "../components/Badge";
import { EmptyState } from "../components/EmptyState";
import { FeedbackThread, ReplyComposer, submissionAsMessage } from "../components/FeedbackThread";
import { Skel } from "../components/Skeleton";
import { usePageMeta } from "../hooks/usePageMeta";

// One of the user's own submissions with the reply thread. Linked from the
// notification bell (kind feedback_reply) and the reply email's CTA.

const TYPE_LABEL: Record<string, string> = {
  bug: "Bug",
  feature: "Feature",
  data: "Missing data",
  other: "Other",
};

function DetailBlock({ obj }: { obj: Record<string, unknown> | null }) {
  const entries = Object.entries(obj ?? {}).filter(([, v]) => v !== null && v !== "" && v !== undefined);
  if (entries.length === 0) return null;
  return (
    <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px", margin: "8px 0 0" }}>
      {entries.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt style={{ fontSize: 12, color: "var(--text-muted)" }}>{k}</dt>
          <dd style={{ fontSize: 12.5, color: "var(--text-secondary)", margin: 0, wordBreak: "break-word" }}>
            {String(v)}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export default function FeedbackDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["feedback", "mine", id],
    queryFn: () => fetchMyFeedbackDetail(id),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  const item = query.data?.feedback;
  usePageMeta({
    title: item ? item.title : "Your feedback",
    description: "Your feedback thread with Atlas.",
    canonicalPath: `/feedback/${id}`,
    noindex: true,
    enabled: !!item,
  });

  const notFound = query.isError && query.error instanceof ApiError && query.error.status === 404;

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["feedback", "mine", id] }),
      queryClient.invalidateQueries({ queryKey: ["feedback", "mine"] }),
    ]);

  return (
    <div
      className="r-page"
      style={{ padding: 28, overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 20 }}
    >
      <Link
        to="/feedback"
        style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-secondary)", textDecoration: "none" }}
      >
        <ArrowLeft size={14} /> Your feedback
      </Link>

      {query.isLoading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Skel height={32} width={280} />
          <Skel height={120} />
        </div>
      ) : notFound || (!query.isLoading && !item) ? (
        <EmptyState
          icon={<MessageSquare size={20} />}
          title={notFound ? "That feedback isn't yours or no longer exists" : "Couldn't load this feedback"}
          hint={notFound ? "Head back to your feedback list." : "Check your connection and try again."}
        />
      ) : item ? (
        <>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
              <Badge variant="neutral">{TYPE_LABEL[item.type] ?? item.type}</Badge>
              {item.lastRepliedAt && <Badge variant="success">Replied</Badge>}
            </div>
            <h1 style={{ font: "var(--type-display)", fontSize: 28, color: "var(--text-primary)", margin: 0, overflowWrap: "anywhere" }}>
              {item.title}
            </h1>
            <DetailBlock obj={item.details} />
            {item.photoUrls.length > 0 && (
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {item.photoUrls.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer">
                    <img
                      src={url}
                      alt="Screenshot attachment"
                      style={{ width: 88, height: 88, objectFit: "cover", borderRadius: "var(--radius-md)", border: "1px solid var(--border-default)", display: "block" }}
                    />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              background: "var(--surface-card)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-lg)",
              boxShadow: "var(--shadow-sm), var(--ring-top)",
              padding: 18,
              minWidth: 0,
            }}
          >
            <FeedbackThread
              viewerRole="user"
              messages={[submissionAsMessage({ id: item.id, body: item.body, createdAt: item.createdAt }), ...item.replies]}
            />
            <ReplyComposer
              send={(body) => replyToMyFeedback(item.id, body)}
              onSent={refresh}
              warning={
                item.replies.length === 0
                  ? "We read everything. If you have more to add, write it here; we'll answer in this thread and by email."
                  : "Replies also go to your email. You can answer here or by replying to that email."
              }
              placeholder="Add to this thread"
              buttonLabel="Send"
            />
          </div>
        </>
      ) : null}
    </div>
  );
}
